import { ReconLabClient } from './client';
import { OsintModule } from './modules/osint';
import { HistoryModule } from './modules/history';

// Export all types
export * from './types';

// Export modules
export { OsintModule, HistoryModule };

/**
 * ReconLab SDK - Main SDK class that provides access to OSINT search and history modules
 */
export class ReconLabSDK {
  private client: ReconLabClient;
  
  // Module instances
  public osint: OsintModule;
  public history: HistoryModule;

  /**
   * Create a new ReconLab SDK instance
   * @param config Configuration options
   */
  constructor(config: { baseURL?: string; timeout?: number } = {}) {
    // Create the API client
    this.client = new ReconLabClient(config);
    
    // Initialize modules
    this.osint = new OsintModule(this.client);
    this.history = new HistoryModule(this.client);
  }

  /**
   * Set API key for authentication
   * @param apiKey The API key
   */
  setApiKey(apiKey: string): void {
    this.client.setApiKey(apiKey);
  }

  /**
   * Get current API key
   * @returns The current API key
   */
  getApiKey(): string | undefined {
    return this.client.getApiKey();
  }

  /**
   * Set wallet address
   * @param walletAddress The wallet address
   */
  setWalletAddress(walletAddress: string): void {
    this.client.setWalletAddress(walletAddress);
  }

  /**
   * Get current wallet address
   * @returns The current wallet address
   */
  getWalletAddress(): string | undefined {
    return this.client.getWalletAddress();
  }

  /**
   * Create a new SDK instance with API key
   * @param apiKey The API key
   * @param config Additional configuration options
   * @returns A new SDK instance with the API key set
   */
  static withApiKey(apiKey: string, config: { baseURL?: string; timeout?: number } = {}): ReconLabSDK {
    const sdk = new ReconLabSDK(config);
    sdk.setApiKey(apiKey);
    return sdk;
  }

  /**
   * Create a new SDK instance with wallet address
   * @param walletAddress The wallet address
   * @param config Additional configuration options
   * @returns A new SDK instance with the wallet address set
   */
  static withWalletAddress(walletAddress: string, config: { baseURL?: string; timeout?: number } = {}): ReconLabSDK {
    const sdk = new ReconLabSDK(config);
    sdk.setWalletAddress(walletAddress);
    return sdk;
  }
}

// Default export
export default ReconLabSDK;
